/**
 * Curated caption languages for the human captioner console.
 *
 * Tags are BCP-47. Viewers compare them against their selected caption
 * language with prefix-style matching ('en-US' satisfies a viewer who picked
 * 'en'), so publishing a well-formed tag is always safe. Free-text input was
 * removed because a typo'd tag silently drops every published line at the
 * viewer's language gate.
 */
export const CAPTIONER_LANGUAGES = [
    { label: 'English (US)', value: 'en-US' },
    { label: 'English (UK)', value: 'en-GB' },
    { label: 'Spanish', value: 'es-ES' },
    { label: 'Spanish (US)', value: 'es-US' },
    { label: 'French', value: 'fr-FR' },
    { label: 'German', value: 'de-DE' },
    { label: 'Italian', value: 'it-IT' },
    { label: 'Portuguese (Brazil)', value: 'pt-BR' },
    { label: 'Dutch', value: 'nl-NL' },
    { label: 'Polish', value: 'pl-PL' },
    { label: 'Russian', value: 'ru-RU' },
    { label: 'Ukrainian', value: 'uk-UA' },
    { label: 'Turkish', value: 'tr-TR' },
    { label: 'Arabic', value: 'ar-SA' },
    { label: 'Hindi', value: 'hi-IN' },
    { label: 'Japanese', value: 'ja-JP' },
    { label: 'Korean', value: 'ko-KR' },
    { label: 'Chinese (Mandarin)', value: 'zh-CN' }
];
export const DEFAULT_CAPTION_LANGUAGE = 'en-US';
// Primary language, optional script subtag, optional region subtag — the
// complete shape a captioner realistically publishes.
const LANGUAGE_TAG_PATTERN = /^[a-zA-Z]{2,3}(?:-[a-zA-Z]{4})?(?:-[a-zA-Z0-9]{2,3})?$/;
/**
 * Canonicalizes a hand-entered or stored language tag without ever returning
 * something malformed: unknown input falls back to the default rather than
 * becoming a tag that cannot match any viewer.
 */
export function normalizeCaptionLanguage(raw) {
    const candidate = (raw ?? '').trim().replace(/_/g, '-');
    if (!candidate || !LANGUAGE_TAG_PATTERN.test(candidate)) {
        return DEFAULT_CAPTION_LANGUAGE;
    }
    const [language, ...subtags] = candidate.split('-');
    // Canonical case: lowercase primary tag, Titlecase script, uppercase region.
    return [
        language.toLowerCase(),
        ...subtags.map(subtag => (subtag.length === 4
            ? subtag.charAt(0).toUpperCase() + subtag.slice(1).toLowerCase()
            : subtag.toUpperCase()))
    ].join('-');
}
